import React, { useEffect, useState } from 'react';
import { BookOpen, AlertTriangle } from 'lucide-react';
import apiService from '../../../services/apiService';

/**
 * D3 read-only accounting policy pack reference. Lazily fetches
 * GET /api/regional-forecast/projects/policy-pack and renders the ASC 350-40 /
 * ASU 2018-15 / ASC 360 decision tree, materiality election, and useful-life
 * buckets. No editing here — this is the citation reference the reviewer reads
 * while resolving classifications.
 */
const CorporateProjectPolicyPanel = () => {
  const [pack, setPack] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || pack) return;
    let cancelled = false;
    apiService.fetchProjectPolicyPack()
      .then((data) => { if (!cancelled) setPack(data); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Could not load the policy pack.'); });
    return () => { cancelled = true; };
  }, [open, pack]);

  return (
    <details className="workspace-change-drawer project-policy-panel" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>
        <BookOpen size={15} aria-hidden="true" /> Accounting policy pack reference
        {pack ? <span>{pack.version}</span> : null}
      </summary>
      <div className="workspace-change-content">
        {error ? (
          <p className="forecast-submit-note"><AlertTriangle size={14} aria-hidden="true" /> {error}</p>
        ) : !pack ? (
          <p className="workspace-empty-state">Loading policy pack…</p>
        ) : (
          <>
            <div className="forecast-subpanel">
              <span className="forecast-section-code">{pack.version}</span>
              <h3>{pack.title}</h3>
              <p className="forecast-submit-note">{pack.purpose}</p>
              {pack.humanReviewPending ? (
                <p className="forecast-health-chip yellow" role="note">
                  <AlertTriangle size={13} aria-hidden="true" /> Human accounting review pending — final ASC/ASU paragraph citations reviewed before production release.
                </p>
              ) : null}
              {pack.materiality ? (
                <p className="forecast-submit-note">
                  <strong>{pack.materiality.label}:</strong> {pack.materiality.text}
                  <br /><em>{pack.materiality.sab99Note}</em>
                </p>
              ) : null}
            </div>
            {(pack.sections || []).map((section) => (
              <div className="forecast-subpanel" key={section.id}>
                <h4>{section.title}</h4>
                <ul className="forecast-submit-note">
                  {(section.rules || []).map((rule) => (
                    <li key={rule.id}>
                      <code className="forecast-mono">{rule.id}</code> · <code className="forecast-mono">{rule.citation}</code>
                      {rule.humanReviewPending ? <span className="forecast-health-chip yellow mini"> review pending</span> : null}
                      <br />{rule.text}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="forecast-subpanel">
              <h4>Useful-life buckets</h4>
              <ul className="forecast-submit-note">
                {(pack.buckets || []).map((bucket) => (
                  <li key={bucket.id}>
                    <strong>{bucket.label}</strong> — {bucket.termMonths} months. {bucket.rationale}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </details>
  );
};

export default CorporateProjectPolicyPanel;
